import { type MouseEvent, useCallback } from 'react'
import moment from 'moment/moment'
import { useLocation, useNavigate } from 'react-router-dom'
import { Avatar, Badge, ChevronRightIcon, cn, Label, Paragraph } from '@quickmd-llc/design-system'
import { getAge, getStateByAbbreviation, isDrugScreenTagClickable, parseUserDateTime } from '@/utils/common'
import { ACCOUNT_ON_HOLD_TEXT, PATIENT_CHART_PAGE_ROUTE } from '@/utils/constants'
import { DATE_FORMAT_SLASH } from '@/utils/dateUtils'
import { type PhoneNumber } from '@/types/patient'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { addRecentlyViewedChartAction } from '@/store/charts/ChartsActions'
import { addRecentlyViewedChart } from '../recentlyViewed'
import { type DrugScreenInfo, type PatientChartListItem } from '../types'
import { getDrugScreenBadge, getIdVerificationBadge, MAIN_ACCOUNT_BADGE } from './patientChartBadges'

const EMPTY_VALUE = '—'

export type DrugScreenFilePayload = {
  PatientId: string
  StateCode: string
  DrugScreen?: DrugScreenInfo | null
}

type Props = {
  patient: PatientChartListItem
  userTimeZone: string | undefined
  onOpenDrugScreenFile: (payload: DrugScreenFilePayload) => void
  onOpenIdVerification: (patientId: string) => void
}

function formatPatientPhone({ CountryCode, Number }: PhoneNumber): string {
  const digits = Number.replace(/\D/g, '')
  if (digits.length < 10) {
    return digits.length ? Number : EMPTY_VALUE
  }

  const last10 = digits.slice(-10)
  const local = `${last10.slice(0, 3)} ${last10.slice(3, 6)} ${last10.slice(6)}`
  return CountryCode ? `+${CountryCode} ${local}` : local
}

function getRestrictionLabel(restrictionStatus?: string): string | undefined {
  if (!restrictionStatus || restrictionStatus === 'none') {
    return undefined
  }

  return restrictionStatus === 'merged' ? 'Merged account' : ACCOUNT_ON_HOLD_TEXT
}

function InfoField({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <Paragraph size='sm' className={cn('text-text-dark', className)}>
      <span className='text-text-medium'>{label} </span>
      {value}
    </Paragraph>
  )
}

export function PatientChartRow({ patient, userTimeZone, onOpenDrugScreenFile, onOpenIdVerification }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const {
    PatientId,
    FirstName,
    LastName,
    ImageUrl,
    Gender,
    BirthDate,
    MedicalRecordNumber,
    StateCode,
    PhoneNumber,
    Email,
    LastSeenDateTime,
    RestrictionStatus,
    IsMergedMainAccount,
    DrugScreen,
    IdentityVerification,
  } = patient

  const fullName = `${FirstName} ${LastName}`
  const genderShort = Gender === 'male' ? 'M' : 'F'
  const dob = moment(BirthDate).format(DATE_FORMAT_SLASH)
  const email = Email || EMPTY_VALUE
  const state = getStateByAbbreviation(StateCode?.toUpperCase()) ?? EMPTY_VALUE
  const lastSeen = LastSeenDateTime ? parseUserDateTime(LastSeenDateTime, userTimeZone, false) : EMPTY_VALUE

  const idBadge = getIdVerificationBadge(IdentityVerification?.Status)
  const drugBadge = getDrugScreenBadge(DrugScreen?.Status)
  const restrictionLabel = getRestrictionLabel(RestrictionStatus)
  const isMergedAccount = RestrictionStatus === 'merged'
  const isDrugFileClickable =
    Boolean(DrugScreen?.Status) && isDrugScreenTagClickable(DrugScreen?.Status, isMergedAccount)

  const handleOpenChart = useCallback(() => {
    addRecentlyViewedChart(patient) // persists to localStorage for cross-session recall
    dispatch(addRecentlyViewedChartAction(patient)) // keeps Redux in sync for live badge updates
    void navigate(`${PATIENT_CHART_PAGE_ROUTE}?id=${PatientId}`, { state: { search: location?.search } })
  }, [PatientId, dispatch, location?.search, navigate, patient])

  const handleOpenIdVerification = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onOpenIdVerification(PatientId)
    },
    [PatientId, onOpenIdVerification],
  )

  const handleOpenDrugScreenFile = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onOpenDrugScreenFile({ PatientId, StateCode: StateCode?.toUpperCase() ?? '', DrugScreen: DrugScreen ?? null })
    },
    [DrugScreen, PatientId, StateCode, onOpenDrugScreenFile],
  )

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={handleOpenChart}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleOpenChart()
        }
      }}
      className='flex cursor-pointer items-center gap-sp-2 border-b border-border-light px-sp-2 py-sp-1.5 last:border-b-0 hover:bg-surface-highlight'>
      <Avatar name={fullName} size='m' className='shrink-0' {...(ImageUrl ? { src: ImageUrl } : {})} />

      <div className='flex min-w-0 flex-1 flex-col gap-sp-0.5'>
        <div className='flex flex-wrap items-center gap-sp-1'>
          <Label size='lg' className='text-text-dark'>
            {fullName}
          </Label>

          <Paragraph size='sm' className='text-text-medium'>
            {getAge(moment(BirthDate).toString())} / {genderShort}
          </Paragraph>

          {idBadge ?
            <button
              type='button'
              disabled={isMergedAccount}
              onClick={handleOpenIdVerification}
              className='rounded-full disabled:pointer-events-none'>
              <Badge size='s' variant={idBadge.variant}>
                {idBadge.label}
              </Badge>
            </button>
          : null}

          {drugBadge && isDrugFileClickable ?
            <button
              type='button'
              disabled={isMergedAccount}
              onClick={handleOpenDrugScreenFile}
              className='rounded-full disabled:pointer-events-none'>
              <Badge size='s' variant={drugBadge.variant}>
                {drugBadge.label}
              </Badge>
            </button>
          : null}

          {drugBadge && !isDrugFileClickable ?
            <Badge size='s' variant={drugBadge.variant}>
              {drugBadge.label}
            </Badge>
          : null}

          {restrictionLabel ?
            <Badge size='s' variant='error'>
              {restrictionLabel}
            </Badge>
          : null}

          {IsMergedMainAccount ?
            <Badge size='s' variant={MAIN_ACCOUNT_BADGE.variant}>
              {MAIN_ACCOUNT_BADGE.label}
            </Badge>
          : null}
        </div>

        <div className='flex flex-wrap items-center gap-x-sp-2 gap-y-sp-0.5'>
          <InfoField label='MRN' value={MedicalRecordNumber} />
          <InfoField label='DOB' value={dob} />
          <InfoField label='Email' value={email} className='whitespace-nowrap' />
          <InfoField label='Phone' value={formatPatientPhone(PhoneNumber)} />
          <InfoField label='State' value={state} />
          <InfoField label='Last seen' value={lastSeen} />
        </div>
      </div>

      <ChevronRightIcon iconSize='s' className='shrink-0 text-text-light' />
    </div>
  )
}
